import React, {useCallback, useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import CustomPasswordInput from "../../_common/component/CustomPasswordInput";
import SubmitButton from "../../_common/component/SubmitButton";
import {REGEX_PASSWORD} from "../../../constant/regexs";
import {lang} from "../../../lang";
import {useModelContext} from "../../../context/ModelContextProvider";
import {account_actions} from "../../../redux/slice/AccountReducer";
import {cache_actions} from "../../../redux/slice/CacheReducer";
import {useNavigate} from "react-router-dom";
import {useDispatch} from "react-redux";
import {UPDATE_PW_INFO_INIT_SET} from "../../../constant/options";
import {useParameters} from "../../../utils/Hooks";
import {useDialogContext} from "../../../context/DialogContextProvider";
import Log from "../../../common/Log";

const UpdatePasswordForm = () => {
    const {account_model, isSuccess, isError} = useModelContext();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {state} = useParameters();
    const {alert} = useDialogContext();

    // state
    const [cur_pw_error, setCurPwError] = useState(false);
    const [new_pw_error, setNewPwError] = useState(false);
    const [check_new_pw_error, setCheckNewPwError] = useState(false);
    const [is_loading, setIsLoading] = useState(false);

    // ref
    const update_pw_info = useRef(UPDATE_PW_INFO_INIT_SET);
    const cur_pw_ref = useRef();
    const new_pw_ref = useRef();
    const check_new_pw_ref = useRef();

    // css style
    const {view} = useComponentStyle();

    // update_pw_info 값의 초기화
    const onInitialUpdatePwInfo = useCallback(() => {
        update_pw_info.current.prev_pw = {
            value: undefined,
            error_msg: null,
        };
        update_pw_info.current.new_pw = {
            value: undefined,
            error_msg: null,
        };
        update_pw_info.current.check_new_pw = {
            value: undefined,
            error_msg: null,
        };
    }, []);

    useEffect(() => {
        // 최초 페이지 렌더시에 기존 비밀번호 input으로 자동 focus
        if (!update_pw_info.current.prev_pw.value) {
            cur_pw_ref.current.focus();
        }

        return () => {
            onInitialUpdatePwInfo();
        };
    }, [onInitialUpdatePwInfo]);

    useEffect(() => {
        // 비번 초기화 -> login -> 비번 변경페이지로 넘어올 때 state에 초기화된 비번 담아서 기존 비밀번호에 그대로 적용
        if (state) {
            update_pw_info.current.prev_pw.value = state.init_pw;
            document.getElementById("id_prev_pw").value = state.init_pw;
            new_pw_ref.current.focus();
        }
    }, [state]);

    const onSubmitChangePassword = async () => {
        setCurPwError(false);
        setNewPwError(false);
        setCheckNewPwError(false);

        let cur_pw = update_pw_info.current.prev_pw;
        let new_pw = update_pw_info.current.new_pw;
        let check_new_pw = update_pw_info.current.check_new_pw;

        // 셋 중 하나라도 값이 없으면 submit X
        if (!cur_pw.value || !new_pw.value || !check_new_pw.value) {
            if (!cur_pw.value) {
                update_pw_info.current.prev_pw.error_msg = lang.modify_password.empty_modify_password;
                setCurPwError(true);
            }
            if (!new_pw.value) {
                update_pw_info.current.new_pw.error_msg = lang.modify_password.empty_modify_password;
                setNewPwError(true);
            }
            if (!check_new_pw.value) {
                update_pw_info.current.check_new_pw.error_msg = lang.modify_password.empty_modify_password;
                setCheckNewPwError(true);
            }
        } else {
            // 셋 다 값이 있음 -> 에러 메세지 체크 : 에러가 있으면 submit X
            if (cur_pw.error_msg || new_pw.error_msg || check_new_pw.error_msg) {
                return false;
            } else {
                // 셋 다 값이 있지만 기존비밀번호와 새 비밀번호가 동일한 경우
                if (cur_pw.value === new_pw.value) {
                    await alert(lang.modify_password.same_as_cur_pw);

                    update_pw_info.current.new_pw = {
                        value: undefined,
                        error_msg: lang.modify_password.need_modify_new_pw,
                    };
                    update_pw_info.current.check_new_pw = {
                        value: undefined,
                        error_msg: lang.modify_password.not_matched_current_password,
                    };

                    setNewPwError(true);
                    setCheckNewPwError(true);
                } else {
                    // 셋 다 값이 있음 -> 에러 메세지가 전부 null -> cur_pw !== new_pw -> new_pw === check_new_pw ->submit
                    setIsLoading(true);

                    const change_password_res = await account_model.putChangePassword({password: new_pw.value});
                    if (isSuccess(change_password_res)) {
                        onInitialUpdatePwInfo();

                        await alert(lang.modify_password.success_update);

                        dispatch(account_actions.doLogout());
                        dispatch(cache_actions.removeAll());

                        navigate("/");
                    } else if (isError(change_password_res)) {
                        Log.private("putChangePassword 에러");
                    }
                    setIsLoading(false);
                }
            }
        }
    };

    return (
        <view.UpdatePasswordFormSection>
            <CustomPasswordInput
                name={"prev_pw"}
                placeholder={"기존 비밀번호를 입력해주세요"}
                need_label={true}
                label_name={"기존 비밀번호"}
                on_change={(text) => (update_pw_info.current.prev_pw.value = text)}
                current_value={update_pw_info.current.prev_pw}
                is_error={cur_pw_error}
                _ref={cur_pw_ref}
            />
            <CustomPasswordInput
                name={"new_pw"}
                placeholder={"4자 이상입력해주세요"}
                need_label={true}
                label_name={"새로운 비밀번호"}
                on_change={(text) => (update_pw_info.current.new_pw.value = text)}
                current_value={update_pw_info.current.new_pw}
                regex={REGEX_PASSWORD}
                is_error={new_pw_error}
                _ref={new_pw_ref}
            />
            <CustomPasswordInput
                name={"check_new_pw"}
                placeholder={"비밀번호를 다시 입력해주세요"}
                on_change={(text) => (update_pw_info.current.check_new_pw.value = text)}
                current_value={update_pw_info.current.check_new_pw}
                is_error={check_new_pw_error}
                _ref={check_new_pw_ref}
            />
            <SubmitButton is_loading={is_loading} button_text={"변경"} on_click={onSubmitChangePassword} />
        </view.UpdatePasswordFormSection>
    );
};

export default UpdatePasswordForm;

export const useComponentStyle = () => {
    return {
        view: {
            UpdatePasswordFormSection: styled.div({
                "& > div:nth-of-type(1), & > div:nth-of-type(3)": {
                    marginBottom: "5em",
                },
                "& > div:nth-of-type(2)": {
                    marginBottom: "2em",
                },
            }),
        },
    };
};
